import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ExternalLink, Plus, CircleDot } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

/**
 * Platforms directory — add a new entry per client tenant here.
 * `status` drives the pill color: "live" (green) | "demo" (amber) | "soon" (grey).
 */
export const PLATFORMS = [
  {
    id: "cargill",
    name: "Cargill",
    url: "https://cargill.instalros.net/",
    descriptionKey: "platforms.cargillDesc",
    status: "live",
    initials: "C",
  },
];

const STATUS_META = {
  live: { label: "LIVE", color: "#0E9F6E" },
  demo: { label: "DEMO", color: "#FFB800" },
  soon: { label: "SOON", color: "#888" },
};

export const PlatformsDropdown = ({ onNavigate }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  // Click-outside closes
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeLater = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  };

  return (
    <div
      ref={wrapRef}
      onMouseEnter={openNow}
      onMouseLeave={closeLater}
      className="hidden md:inline-block relative"
      data-testid="nav-platforms-wrapper"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="nav-platforms-btn"
        className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-mono uppercase tracking-[0.18em] rounded-sm border transition-colors ${
          open
            ? "bg-[#0066FF] text-white border-[#0066FF]"
            : "border-[#0066FF]/60 text-[#4DA3FF] hover:bg-[#0066FF] hover:text-white hover:border-[#0066FF]"
        }`}
      >
        {t("nav.platforms")}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="nav-platforms-menu"
          className="absolute right-0 top-[calc(100%+8px)] w-[320px] bg-[#0A0D14] border border-white/10 rounded-sm shadow-2xl overflow-hidden"
        >
          {/* Header strip */}
          <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between bg-[#050505]">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4DA3FF]">
              ◉ {t("platforms.directory")}
            </p>
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
              {PLATFORMS.length} {PLATFORMS.length === 1 ? t("platforms.tenant") : t("platforms.tenants")}
            </span>
          </div>

          {/* Tenants */}
          <div className="divide-y divide-white/[0.06]">
            {PLATFORMS.map((p) => {
              const meta = STATUS_META[p.status] || STATUS_META.live;
              return (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid={`platforms-item-${p.id}`}
                  role="menuitem"
                  className="flex items-center gap-3 px-4 py-3.5 hover:bg-[#0066FF]/[0.12] transition-colors group"
                >
                  <div className="w-9 h-9 shrink-0 rounded-sm bg-[#0066FF]/15 border border-[#0066FF]/40 flex items-center justify-center font-mono text-sm text-[#4DA3FF]">
                    {p.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white truncate">{p.name}</p>
                      <span
                        className="font-mono text-[8.5px] px-1.5 py-[1px] rounded-sm"
                        style={{
                          color: meta.color,
                          backgroundColor: `${meta.color}1A`,
                          border: `1px solid ${meta.color}40`,
                        }}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-white/55 truncate">
                      {t(p.descriptionKey)}
                    </p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-white/30 group-hover:text-[#4DA3FF] transition-colors" />
                </a>
              );
            })}
          </div>

          {/* Footer CTA — onboard a new tenant */}
          <a
            href="#footprint"
            onClick={() => {
              setOpen(false);
              if (onNavigate) onNavigate();
            }}
            data-testid="platforms-onboard-cta"
            className="flex items-center gap-3 px-4 py-3 border-t border-white/[0.06] bg-[#050505] hover:bg-[#0066FF]/[0.1] transition-colors"
          >
            <div className="w-9 h-9 shrink-0 rounded-sm border border-dashed border-white/20 flex items-center justify-center">
              <Plus className="w-4 h-4 text-white/50" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#4DA3FF]">
                {t("platforms.onboardTitle")}
              </p>
              <p className="mt-0.5 text-[11px] text-white/55 truncate">
                {t("platforms.onboardDesc")}
              </p>
            </div>
            <CircleDot className="w-3.5 h-3.5 text-white/30" />
          </a>
        </div>
      )}
    </div>
  );
};

/**
 * Mobile variant — renders inside the hamburger panel as an expandable list.
 */
export const PlatformsMobileSection = ({ onLinkClick }) => {
  const { t } = useI18n();
  return (
    <div data-testid="mobile-nav-platforms-section" className="py-2">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4DA3FF] px-1">
        {t("nav.platforms")} · {PLATFORMS.length}
      </p>
      <div className="mt-2 border border-[#0066FF]/30 rounded-sm overflow-hidden divide-y divide-white/[0.06]">
        {PLATFORMS.map((p) => {
          const meta = STATUS_META[p.status] || STATUS_META.live;
          return (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onLinkClick}
              data-testid={`mobile-platforms-item-${p.id}`}
              className="flex items-center gap-3 px-3 py-3 hover:bg-[#0066FF]/[0.12] transition-colors"
            >
              <div className="w-8 h-8 shrink-0 rounded-sm bg-[#0066FF]/15 border border-[#0066FF]/40 flex items-center justify-center font-mono text-xs text-[#4DA3FF]">
                {p.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-white">{p.name}</p>
                  <span
                    className="font-mono text-[8.5px] px-1 py-[1px] rounded-sm"
                    style={{
                      color: meta.color,
                      backgroundColor: `${meta.color}1A`,
                      border: `1px solid ${meta.color}40`,
                    }}
                  >
                    {meta.label}
                  </span>
                </div>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-white/30" />
            </a>
          );
        })}
        <a
          href="#footprint"
          onClick={onLinkClick}
          data-testid="mobile-platforms-onboard"
          className="flex items-center gap-3 px-3 py-3 hover:bg-[#0066FF]/[0.1] transition-colors"
        >
          <div className="w-8 h-8 shrink-0 rounded-sm border border-dashed border-white/20 flex items-center justify-center">
            <Plus className="w-4 h-4 text-white/50" />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#4DA3FF]">
            {t("platforms.onboardTitle")}
          </p>
        </a>
      </div>
    </div>
  );
};
